import React, { useMemo, useState } from 'react';
import { FiCheck, FiCopy, FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';

import {
  CopiedToast,
  Field,
  PasswordActions,
  PasswordShell,
  PwdIconButton,
  StrengthBar,
  StrengthLabel,
} from './styles';

interface Props {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: string;
  autoComplete?: string;
  showStrength?: boolean;
  allowGenerate?: boolean;
}

const CHARSET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*?';

const generatePassword = (length = 14): string => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, n => CHARSET[n % CHARSET.length]).join('');
  }
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += CHARSET[Math.floor(Math.random() * CHARSET.length)];
  }
  return out;
};

const strengthOf = (pwd: string): 0 | 1 | 2 | 3 | 4 => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score += 1;
  return Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
};

const STRENGTH_LABEL: Record<number, string> = {
  0: 'Vazio',
  1: 'Fraca',
  2: 'Razoável',
  3: 'Boa',
  4: 'Forte',
};

const PasswordField: React.FC<Props> = ({
  id,
  label,
  value,
  placeholder,
  onChange,
  onBlur,
  error,
  autoComplete,
  showStrength = true,
  allowGenerate = true,
}) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const level = useMemo(() => strengthOf(value), [value]);

  const handleGenerate = () => {
    const pwd = generatePassword();
    onChange(pwd);
    setVisible(true);
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // no-op (clipboard not available)
    }
  };

  return (
    <Field>
      <label htmlFor={id}>
        {label}
        {copied && (
          <CopiedToast>
            <FiCheck size={12} />
            Copiado
          </CopiedToast>
        )}
      </label>
      <PasswordShell>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          autoComplete={autoComplete || 'new-password'}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
        />
        <PasswordActions>
          <PwdIconButton
            type="button"
            onClick={() => setVisible(v => !v)}
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            title={visible ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {visible ? <FiEyeOff size={15} /> : <FiEye size={15} />}
          </PwdIconButton>
          <PwdIconButton
            type="button"
            onClick={handleCopy}
            disabled={!value}
            aria-label="Copiar senha"
            title="Copiar senha"
          >
            <FiCopy size={14} />
          </PwdIconButton>
          {allowGenerate && (
            <PwdIconButton
              type="button"
              $accent
              onClick={handleGenerate}
              aria-label="Gerar senha forte"
              title="Gerar senha forte"
            >
              <FiRefreshCw size={14} />
            </PwdIconButton>
          )}
        </PasswordActions>
      </PasswordShell>
      {showStrength && (
        <>
          <StrengthBar $level={level}>
            <span className="segment" />
            <span className="segment" />
            <span className="segment" />
            <span className="segment" />
          </StrengthBar>
          <StrengthLabel $level={level}>{STRENGTH_LABEL[level]}</StrengthLabel>
        </>
      )}
      {error && <span className="error">{error}</span>}
    </Field>
  );
};

PasswordField.defaultProps = {
  placeholder: '',
  onBlur: undefined,
  error: undefined,
  autoComplete: undefined,
  showStrength: false,
  allowGenerate: false,
};

export default PasswordField;
